import { ImportResult } from './types';

/**
 * Nhập nhiều file ngày một lượt (tab Data ngày): mỗi file tự xem trước, máy chủ đọc cột
 * "Giai đoạn dữ liệu" để biết file là ngày nào. Ở đây chỉ có hàm thuần: gom theo ngày,
 * nhận ra file trùng ngày và ngày còn trống trong khoảng của đợt.
 */

/** Một đợt tối đa bao nhiêu file (một tháng dài nhất 31 ngày). */
export const BATCH_MAX_FILES = 31;

export type BatchStatus =
  | 'queued' // chờ xem trước
  | 'reading' // đang xem trước
  | 'ready' // xem trước xong, nhập được
  | 'blocked' // máy chủ chặn (file cộng dồn, ngày tương lai…)
  | 'error' // không đọc được file
  | 'applying'
  | 'done'
  | 'failed'; // áp dụng lỗi

export interface BatchRow {
  key: string;
  file: File;
  status: BatchStatus;
  /** Có nhập file này trong lần bấm "Nhập" hay không. */
  selected: boolean;
  result?: ImportResult;
  /** Câu lỗi (đọc file / áp dụng). */
  error?: string;
}

/** Ngày của file theo máy chủ (DAILY trả `day`; file chặn vẫn có `periods`). */
export function rowDay(row: BatchRow): string | null {
  const r = row.result;
  if (!r) return null;
  if (r.day) return r.day;
  const p = r.periods.length === 1 ? r.periods[0] : null;
  return p && p.start === p.end ? p.start : null;
}

/** Dấu vân tay số liệu: hai file cùng ngày có dấu giống nhau thì coi là một. */
export function rowFingerprint(row: BatchRow): string {
  const r = row.result;
  if (!r) return '';
  return [r.summary.rows, r.summary.creators, r.totals.diamonds, r.totals.liveSeconds].join('|');
}

const canPick = (row: BatchRow) => row.status === 'ready' || row.status === 'failed';

/**
 * Chọn mặc định sau khi xem trước xong: mỗi ngày chỉ một file.
 * - Ngày có một file nhập được → chọn.
 * - Nhiều file cùng ngày, số liệu giống hệt → chọn file đầu, các file sau là bản lặp.
 * - Nhiều file cùng ngày, số liệu khác nhau → không chọn file nào, người nhập tự chọn.
 */
export function defaultSelection(rows: BatchRow[]): BatchRow[] {
  const byDay = new Map<string, BatchRow[]>();
  rows.forEach((row) => {
    const day = rowDay(row);
    if (!day || !canPick(row)) return;
    byDay.set(day, [...(byDay.get(day) || []), row]);
  });
  const chosen = new Set<string>();
  byDay.forEach((group) => {
    const prints = new Set(group.map(rowFingerprint));
    if (prints.size === 1) chosen.add(group[0].key);
  });
  return rows.map((row) => ({ ...row, selected: chosen.has(row.key) }));
}

/** Chọn / bỏ một file; chọn thì bỏ các file khác cùng ngày (mỗi ngày một file). */
export function toggleRow(rows: BatchRow[], key: string, selected: boolean): BatchRow[] {
  const target = rows.find((row) => row.key === key);
  if (!target) return rows;
  const day = rowDay(target);
  return rows.map((row) => {
    if (row.key === key) return { ...row, selected };
    if (selected && day && rowDay(row) === day) return { ...row, selected: false };
    return row;
  });
}

export interface DayGroupInfo {
  /** Số file cùng ngày (kể cả file này). */
  count: number;
  /** Mọi file cùng ngày có số liệu giống hệt. */
  identical: boolean;
}

/** Thông tin trùng ngày của từng file (theo key). */
export function dayGroups(rows: BatchRow[]): Map<string, DayGroupInfo> {
  const byDay = new Map<string, BatchRow[]>();
  rows.forEach((row) => {
    const day = rowDay(row);
    if (day) byDay.set(day, [...(byDay.get(day) || []), row]);
  });
  const out = new Map<string, DayGroupInfo>();
  byDay.forEach((group) => {
    const identical = new Set(group.map(rowFingerprint)).size === 1;
    group.forEach((row) => out.set(row.key, { count: group.length, identical }));
  });
  return out;
}

const addDay = (iso: string, delta: number) => {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + delta);
  return date.toISOString().slice(0, 10);
};

/** Ngày nằm giữa ngày sớm nhất và muộn nhất của đợt mà không có file nào. */
export function gapDays(days: string[]): string[] {
  const sorted = Array.from(new Set(days)).sort();
  if (sorted.length < 2) return [];
  const have = new Set(sorted);
  const gaps: string[] = [];
  for (let d = sorted[0]; d < sorted[sorted.length - 1]; d = addDay(d, 1)) {
    if (!have.has(d)) gaps.push(d);
    if (gaps.length > 62) break;
  }
  return gaps;
}

/** Thứ tự hiển thị: theo ngày tăng dần, file chưa biết ngày xuống cuối. */
export function sortRows(rows: BatchRow[]): BatchRow[] {
  return [...rows].sort((a, b) => {
    const da = rowDay(a) || '9999';
    const db = rowDay(b) || '9999';
    return da === db ? a.file.name.localeCompare(b.file.name) : da < db ? -1 : 1;
  });
}

/** Sẽ ghi đè số đã có (ngày đã nhập trước đó). */
export function isOverwrite(row: BatchRow): boolean {
  const r = row.result;
  return (
    !!r &&
    (!!r.existing ||
      r.warnings.some((w) => w.code === 'ALREADY_IMPORTED' || w.code === 'MANUAL_OVERWRITE'))
  );
}
